import { useQuery } from "@tanstack/react-query";

import { USER_API } from "config";
import { useAxios } from "hooks/useAxios";
import { notificationKeys } from "queries";
import type { NotificationCounts } from "./types";

type CountResponse = { data?: unknown[]; meta?: { item_count?: number } } | unknown[];

const NO_COUNTS: NotificationCounts = { all: 0, unread: 0, read: 0 };

/**
 * The true total for a filter: `meta.item_count` from GET /notifications.
 * A response without it carries one page only, and a page length is not a
 * total, so the total is unknown instead of guessed.
 */
const readItemCount = (response: CountResponse): number | null => {
  const itemCount = Array.isArray(response) ? undefined : response.meta?.item_count;
  return typeof itemCount === "number" ? itemCount : null;
};

/**
 * The signed-in user's notification totals from the server: all, unread
 * and read. Counting a loaded page in the browser capped the numbers at the
 * page size (the badge read at most 50, the page at most 10).
 *
 * Two take=1 requests read `meta.item_count` for no filter and for
 * is_read=false; read is the difference. The key sits under
 * `notificationKeys.all`, so mark as read, delete and clear all refresh it
 * with the list.
 *
 * Unknown totals resolve to `null` as a successful result rather than an
 * error. That replaces any earlier totals in the cache, so a later failed
 * refetch cannot bring old numbers back on screen.
 */
export function useNotificationCounts(): {
  /** Zeros until real totals arrive. A badge that hides at zero can read it directly. */
  counts: NotificationCounts;
  /** True only when the server returned real totals. Anything that prints them must check it. */
  hasTotals: boolean;
  isLoading: boolean;
  /** The count requests failed and no totals are known. */
  isError: boolean;
  refetch: () => Promise<unknown>;
} {
  const axios = useAxios({ allResponse: false });

  const query = useQuery({
    queryKey: notificationKeys.counts(),
    queryFn: async (): Promise<NotificationCounts | null> => {
      const [all, unread] = await Promise.all([
        axios.get(`${USER_API.notifications}?take=1&page=1`),
        axios.get(`${USER_API.notifications}?is_read=false&take=1&page=1`),
      ]);
      const allCount = readItemCount(all as unknown as CountResponse);
      const unreadCount = readItemCount(unread as unknown as CountResponse);
      if (allCount === null || unreadCount === null) return null;
      return {
        all: allCount,
        unread: unreadCount,
        read: Math.max(0, allCount - unreadCount),
      };
    },
  });

  return {
    counts: query.data ?? NO_COUNTS,
    hasTotals: query.data != null,
    isLoading: query.isLoading,
    isError: query.isError && query.data == null,
    refetch: query.refetch,
  };
}
